import { useTranslation } from 'react-i18next';

import { IconButton, Tooltip } from '@cfa/react-core';
import { InfoOutline } from '@cfa/system-icons';

import { formatDate } from '@/utils/date';

import { FolderChecked, Incomplete, Ribbon } from '@/icons';

interface AssignmentStatusSummaryProps {
  completedStatusText: string;
  isCurrentAssignmentCompleted: boolean;
  previousAssignmentCompletionDate?: string;
  previousAssignmentTimeSpentHrs: number;
  previousAssignmentTimeSpentMins: number;
  timeSpentHrs: number;
  timeSpentMins: number;
}

const AssignmentStatusSummary: React.FC<AssignmentStatusSummaryProps> = ({
  completedStatusText,
  isCurrentAssignmentCompleted,
  previousAssignmentCompletionDate,
  previousAssignmentTimeSpentHrs,
  previousAssignmentTimeSpentMins,
  timeSpentHrs,
  timeSpentMins,
}) => {
  const { t } = useTranslation();

  const infoTooltip = (
    <Tooltip.Root delay={250}>
      <IconButton className="ml-1! p-0!" data-testid="AssignmentStatusInfoIcon">
        <InfoOutline size="sm" />
      </IconButton>
      <Tooltip.Content>
        {t('TrainingPlans.timeSpentOnPlanTooltip')}
      </Tooltip.Content>
    </Tooltip.Root>
  );

  if (previousAssignmentCompletionDate && !isCurrentAssignmentCompleted) {
    return (
      <div className="flex flex-col gap-1 pt-2 pb-6">
        <div className="flex items-center gap-1">
          <Incomplete className="size-5" />
          <p
            className="text-variant-body2 text-grayscale-gray6 font-medium"
            data-testid="time-spent-on-plan-text-team-member"
          >
            {t(
              timeSpentHrs > 0
                ? 'TrainingPlans.accountability.currentAssignmentHoursAndMinutes'
                : 'TrainingPlans.accountability.currentAssignmentMinutes',
              {
                hoursAmount: timeSpentHrs,
                hoursFormatted:
                  timeSpentHrs === 1
                    ? t('Generic.hours_one')
                    : t('Generic.hours_other'),
                minutesAmount: timeSpentMins,
                minutesFormatted:
                  timeSpentMins === 1
                    ? t('Generic.minutes_one')
                    : t('Generic.minutes_other'),
                status: t('Generic.incomplete'),
              },
            )}
          </p>
          {infoTooltip}
        </div>
        <div className="flex items-center gap-1">
          <FolderChecked className="size-5" />
          <p
            className="text-variant-body2 text-grayscale-gray6"
            data-testid="previous-assignment-date"
          >
            {t(
              previousAssignmentTimeSpentHrs > 0
                ? 'TrainingPlans.accountability.previousAssignmentHoursAndMinutes'
                : 'TrainingPlans.accountability.previousAssignmentMinutes',
              {
                date: formatDate(previousAssignmentCompletionDate),
                hoursAmount: previousAssignmentTimeSpentHrs,
                hoursFormatted:
                  previousAssignmentTimeSpentHrs === 1
                    ? t('Generic.hours_one')
                    : t('Generic.hours_other'),
                minutesAmount: previousAssignmentTimeSpentMins,
                minutesFormatted:
                  previousAssignmentTimeSpentMins === 1
                    ? t('Generic.minutes_one')
                    : t('Generic.minutes_other'),
              },
            )}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="text-variant-body1 pb-header-content-spacing pt-5 font-medium">
      <div className="flex items-center gap-1">
        {isCurrentAssignmentCompleted ? (
          <div className="inline-block">
            <Ribbon height={24} width={24} />
          </div>
        ) : (
          <Incomplete height={24} width={24} />
        )}
        <p data-testid="time-spent-on-plan-text-team-member">
          {isCurrentAssignmentCompleted
            ? completedStatusText
            : `${t('Generic.incomplete')}, ${timeSpentHrs > 0 ? `${timeSpentHrs} ${t('Generic.hours', { count: timeSpentHrs })} ` : ''}${timeSpentMins} ${t('Generic.minutes', { count: timeSpentMins })} ${t('Generic.minsSpent')}`}
        </p>
        {infoTooltip}
      </div>
    </div>
  );
};

export default AssignmentStatusSummary;
